import { addToLocalStorage, getFromLocalStorage } from '@aarhus-university/au-lib-react-components/src/lib/helpers';
import { isImpersonating } from '@aarhus-university/au-lib-react-components/src/lib/portals';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';

export const shortCellSize = 9;
export const shortestCellSize = 6;
export const cellFraction = 60 / 5;

export const isImpersonationError = (
  error: FetchBaseQueryError,
): boolean => {
  if (typeof error.status !== 'number') {
    return false;
  }

  return error.status === 403 && isImpersonating();
};

const orgUnitPattern = /\(([^)]+)\)/;
export const stripOrgUnitName = (orgUnitName: string): string => orgUnitName.replace(orgUnitPattern, '').trim();

export const getFirstEducation = (educations: IEnrolment[]): IEnrolment | undefined => {
  const activeEducations = educations.filter((enrolment) => enrolment.active);
  if (activeEducations.length > 0) {
    const sortedByStart = activeEducations.sort((a, b) => (a.start > b.start ? -1 : 1));
    const first = sortedByStart[0];
    if (first && !first.ended) {
      return first;
    }
  }
  return undefined;
};

export const getDiagrammeMatrix = (data: IDiagrammeElement[]): {
  maxPeriod: number,
  maxSortX: number,
  matrix: IDiagrammeElement[][],
} => {
  // Find det maksimale antal rækker og kolonner
  const maxPeriod = Math.max(...data.map((item) => item.period));
  const maxSortX = Math.max(...data.map((item) => item.sortX));

  // Opret en tom matrix til tabellen
  // eslint-disable-next-line max-len
  const tableMatrix = Array.from({ length: maxPeriod + 1 }, () => Array(maxSortX + 1).fill(null));

  // Fyld matrixen med data
  data.forEach((item) => {
    tableMatrix[item.period][item.sortX] = item;
  });

  return {
    matrix: tableMatrix,
    maxPeriod,
    maxSortX,
  };
};

export const getFilterPayload = (
  educationsFromData: number[],
  facultiesFromData: number[],
  campusesFromData: number[],
): {
  educations: number[],
  faculties: number[],
  campuses: number[],
  skip: boolean,
} => {
  const educations = typeof window.IMPERSONATE_EDUCATIONS !== 'undefined' ? window.IMPERSONATE_EDUCATIONS : educationsFromData;
  const faculties = typeof window.IMPERSONATE_FACULTIES !== 'undefined' ? window.IMPERSONATE_FACULTIES : facultiesFromData;
  const campuses = typeof window.IMPERSONATE_CAMPUSES !== 'undefined' ? window.IMPERSONATE_CAMPUSES : campusesFromData;
  const skip = educations.length === 0; // && faculties.length === 0 && campuses.length === 0;
  return {
    educations,
    faculties,
    campuses,
    skip,
  };
};

export const getCalloutClass = (type: number): string => {
  let className = '';
  if (type === 0) {
    className = ' header-callout--warning';
  }
  if (type === 1) {
    className = ' header-callout--attention';
  }
  if (type === 2) {
    className = ' header-callout--confirm';
  }
  return className;
};

export const getNotificationType = (type: number): string => {
  let typeStr = 'default';
  if (type === 0) {
    typeStr = 'warning';
  }
  if (type === 1) {
    typeStr = 'attention';
  }
  if (type === 2) {
    typeStr = 'confirm';
  }
  return typeStr;
};

const systemMessageKey = 'systemmessages';
export const dismissNotification = (message: ISystemMessage): void => {
  const existing = getFromLocalStorage(systemMessageKey) as ISystemMessage[] | null;
  if (existing) {
    if (!existing.find((e) => e.id === message.id)) {
      existing.push(message);
      addToLocalStorage(systemMessageKey, existing);
    }
  } else {
    addToLocalStorage(systemMessageKey, [message]);
  }
};

// eslint-disable-next-line max-len
export const getDismissedNotifications = (): ISystemMessage[] => getFromLocalStorage(systemMessageKey) as ISystemMessage[] || [];
